/* Local-only device checks. No recording, persistence, or media network requests. */
(() => {
  'use strict';
  const root = document.querySelector('[data-mm-meeting-readiness-check]');
  if (!root || root.dataset.readyInitialized) return;
  root.dataset.readyInitialized = 'true';
  const el = name => root.querySelector(`[data-ready-${name}]`);
  const media = navigator.mediaDevices;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  const supported = window.isSecureContext && media && typeof media.getUserMedia === 'function';
  const checks = Array.from(root.querySelectorAll('[data-ready-check]'));
  const states = { mic: { stream: null, pending: false, token: 0, timer: 0 }, camera: { stream: null, pending: false, token: 0, timer: 0 } };
  let audio = null, source = null, analyser = null, wave = null, frame = 0;
  let micObserved = false, cameraObserved = false, micAnalysisPaused = false, sustainedSignal = 0, lastTick = 0, lastLight = 0;
  let toneContext = null, toneToken = 0, reportURL = null, reportTimer = 0, deviceListToken = 0;
  const video = el('video');
  const lightCanvas = document.createElement('canvas');
  lightCanvas.width = 48; lightCanvas.height = 27;
  let light = null;
  try { light = lightCanvas.getContext('2d', { willReadFrequently: true }); } catch (_) { /* Brightness is optional; the camera preview remains useful. */ }
  video.muted = true;
  function status(kind, message) { el(`${kind}-status`).textContent = message; }
  function closeContext(context) {
    if (!context || context.state === 'closed') return;
    try { Promise.resolve(context.close()).catch(() => {}); } catch (_) { /* The context may already be closing. */ }
  }
  function controls() {
    for (const kind of ['mic', 'camera']) {
      const s = states[kind];
      el(`${kind}-start`).disabled = !supported || s.pending || !!s.stream || (kind === 'mic' && !AudioContextClass);
      el(`${kind}-stop`).disabled = !s.pending && !s.stream;
      el(`${kind}-device`).disabled = s.pending || el(`${kind}-device`).options.length < 2;
    }
    el('stop-all').disabled = !toneContext && !Object.values(states).some(s => s.stream || s.pending);
  }
  function summary() {
    const complete = checks.filter(c => c.checked).length + Number(micObserved) + Number(cameraObserved || el('skip-camera').checked);
    el('progress').value = complete;
    el('summary').textContent = complete === 7
      ? '7 of 7 checks completed. Stop device access, then confirm the same devices and mute state in your meeting app.'
      : `${complete} of 7 checks completed. Finish the remaining checks before joining.`;
  }
  function stop(kind, message) {
    const s = states[kind];
    s.token++; s.pending = false;
    clearTimeout(s.timer); s.timer = 0;
    if (s.stream) s.stream.getTracks().forEach(track => track.stop());
    s.stream = null;
    if (kind === 'mic') {
      if (source) { try { source.disconnect(); } catch (_) { /* already disconnected */ } }
      closeContext(audio); audio = null; analyser = null; source = null; wave = null;
      micAnalysisPaused = false;
      el('level').value = 0; el('db').textContent = '—';
    } else { video.pause(); video.srcObject = null; }
    if (message) status(kind, message);
    if (!states.mic.stream && !states.camera.stream) { cancelAnimationFrame(frame); frame = 0; }
    controls();
  }
  function stopTone() {
    toneToken++; closeContext(toneContext); toneContext = null;
    el('tone').disabled = !AudioContextClass;
    el('tone-status').textContent = 'No sound is playing.';
    controls();
  }
  function stopAll(message) { stop('mic', message); stop('camera', message); stopTone(); }
  function errorText(error, kind) {
    const name = kind === 'mic' ? 'microphone' : 'camera';
    const messages = {
      NotAllowedError: `Access was not allowed. Open your browser’s site permissions and system privacy settings, allow this ${name}, and try again.`,
      NotFoundError: `No ${name} was found. Connect one and try again.`,
      NotReadableError: `The ${name} could not be opened. Close other apps using it, reconnect it, and retry.`,
      OverconstrainedError: `That ${name} is unavailable. Select System default and retry.`,
      AbortError: `The ${name} test was interrupted. Please try again.`
    };
    return messages[error.name] || `The ${name} test could not start. Try a full browser tab, check device permissions, and retry.`;
  }
  async function devices() {
    if (!media || !media.enumerateDevices) return;
    const token = ++deviceListToken;
    try {
      const list = await media.enumerateDevices();
      if (token !== deviceListToken) return;
      for (const kind of ['mic', 'camera']) {
        const select = el(`${kind}-device`), selected = select.value;
        const inputs = list.filter(d => d.kind === (kind === 'mic' ? 'audioinput' : 'videoinput') && d.deviceId);
        select.replaceChildren(new Option('System default', ''));
        inputs.forEach((d, i) => select.add(new Option(d.label || `${kind === 'mic' ? 'Microphone' : 'Camera'} ${i + 1}`, d.deviceId)));
        if (inputs.some(d => d.deviceId === selected)) select.value = selected;
      }
      controls();
    } catch (_) { /* Device selection is optional; default capture remains available. */ }
  }
  async function start(kind) {
    const s = states[kind];
    if (!supported || s.pending || s.stream) return;
    if (kind === 'mic' && !AudioContextClass) return;
    const token = ++s.token;
    s.pending = true;
    if (kind === 'mic') { micObserved = false; sustainedSignal = 0; el('signal').textContent = 'Waiting for sound'; }
    else { cameraObserved = false; el('skip-camera').checked = false; el('resolution').textContent = '—'; el('brightness').textContent = '—'; }
    summary(); controls();
    status(kind, 'Waiting for permission. Use the browser’s prompt, or press Stop to cancel this test.');
    let newStream = null, context = null;
    try {
      if (kind === 'mic') { context = new AudioContextClass(); audio = context; await context.resume(); }
      if (token !== s.token) { closeContext(context); return; }
      const selected = el(`${kind}-device`).value;
      const constraint = selected ? { deviceId: { exact: selected } } : {};
      const request = kind === 'mic' ? { audio: { ...constraint, echoCancellation: true, noiseSuppression: true }, video: false } : { audio: false, video: { ...constraint, width: { ideal: 1280 }, height: { ideal: 720 } } };
      newStream = await media.getUserMedia(request);
      if (token !== s.token || document.hidden) { newStream.getTracks().forEach(t => t.stop()); closeContext(context); if (token === s.token) stop(kind, 'Test stopped while this page was hidden.'); return; }
      s.stream = newStream; s.pending = false;
      for (const track of newStream.getTracks()) track.addEventListener('ended', () => {
        if (s.stream !== newStream) return;
        if (kind === 'mic') { micObserved = false; el('signal').textContent = 'Disconnected'; } else cameraObserved = false;
        stop(kind, 'Device disconnected or access ended. Reconnect and start the test again.'); summary();
      });
      // Start the access limit as soon as a stream opens, even if playback stalls.
      s.timer = setTimeout(() => {
        if (token === s.token) stop(kind, 'Device access stopped after five minutes. Start again if you need more time.');
      }, 300000);
      if (kind === 'mic') {
        source = context.createMediaStreamSource(newStream); analyser = context.createAnalyser(); analyser.fftSize = 2048;
        wave = new Float32Array(analyser.fftSize); source.connect(analyser);
        status(kind, 'Microphone is active. Speak at your normal meeting volume. Audio is not recorded or played through your speakers.');
      } else {
        video.srcObject = newStream;
        await video.play();
        if (token !== s.token) return;
        status(kind, 'Camera is active. Adjust your position and lighting while looking at the mirrored preview.');
      }
      controls(); devices();
      if (!frame) frame = requestAnimationFrame(tick);
    } catch (error) {
      if (newStream) newStream.getTracks().forEach(t => t.stop());
      closeContext(context);
      if (token === s.token) stop(kind, errorText(error, kind));
    }
  }
  function tick(now) {
    frame = 0;
    if (now - lastTick >= 100) {
      lastTick = now;
      if (analyser && wave && (!audio || audio.state !== 'running')) {
        // A suspended/interrupted context can return stale samples or silence.
        // Neither is a current microphone measurement or a new observation.
        sustainedSignal = 0;
        el('level').value = 0;
        el('db').textContent = 'Unavailable';
        const pausedSignal = micObserved ? 'Signal detected earlier; analysis paused' : 'Analysis paused';
        if (el('signal').textContent !== pausedSignal) el('signal').textContent = pausedSignal;
        if (!micAnalysisPaused) {
          status('mic', 'Microphone analysis is paused by the browser. No live level is available. Stop the microphone test, then start it again to retry.' + (micObserved ? ' Your earlier signal observation is still saved in this checklist.' : ''));
          micAnalysisPaused = true;
        }
      } else if (analyser && wave) {
        if (micAnalysisPaused) {
          micAnalysisPaused = false;
          status('mic', 'Microphone analysis resumed. Speak at your normal meeting volume. Audio is not recorded or played through your speakers.');
        }
        analyser.getFloatTimeDomainData(wave);
        let energy = 0, peak = 0;
        for (const sample of wave) { energy += sample * sample; peak = Math.max(peak, Math.abs(sample)); }
        const rms = Math.sqrt(energy / wave.length), db = rms > 0 ? Math.max(-90, 20 * Math.log10(rms)) : -90;
        el('db').textContent = db <= -90 ? 'Below −90 dBFS' : `${Math.round(db)} dBFS`;
        el('level').value = Math.max(0, Math.min(100, (db + 60) / 60 * 100));
        sustainedSignal = db > -50 ? sustainedSignal + 1 : 0;
        if (sustainedSignal >= 3 && !micObserved) { micObserved = true; summary(); }
        const signalText = peak >= .98 ? 'Near clipping' : micObserved ? 'Signal detected' : 'Waiting for sound';
        if (el('signal').textContent !== signalText) el('signal').textContent = signalText;
      }
      if (states.camera.stream && video.readyState >= 2 && video.videoWidth) {
        if (!cameraObserved) { cameraObserved = true; summary(); }
        el('resolution').textContent = `${video.videoWidth} × ${video.videoHeight}`;
        if (light && now - lastLight > 750) {
          lastLight = now;
          try {
            light.drawImage(video, 0, 0, 48, 27);
            const pixels = light.getImageData(0, 0, 48, 27).data;
            let sum = 0;
            for (let i = 0; i < pixels.length; i += 4) sum += .2126 * pixels[i] + .7152 * pixels[i + 1] + .0722 * pixels[i + 2];
            el('brightness').textContent = `${Math.round(sum / (pixels.length / 4) / 255 * 100)} / 100`;
          } catch (_) { el('brightness').textContent = 'Unavailable'; }
        }
      }
    }
    if (states.mic.stream || states.camera.stream) frame = requestAnimationFrame(tick);
  }
  async function playTone() {
    if (!AudioContextClass || toneContext) return;
    const token = ++toneToken;
    let context = null;
    try {
      context = new AudioContextClass(); toneContext = context; el('tone').disabled = true; controls();
      await context.resume();
      if (token !== toneToken) return;
      const oscillator = context.createOscillator(), gain = context.createGain();
      oscillator.frequency.value = 440;
      gain.gain.setValueAtTime(0, context.currentTime);
      gain.gain.linearRampToValueAtTime(.08, context.currentTime + .06);
      gain.gain.setValueAtTime(.08, context.currentTime + .7);
      gain.gain.linearRampToValueAtTime(0, context.currentTime + .85);
      oscillator.connect(gain); gain.connect(context.destination);
      oscillator.onended = () => { closeContext(context); if (token !== toneToken) return; toneContext = null; el('tone').disabled = false; el('tone-status').textContent = 'Tone finished. If you heard it clearly, check the speaker item.'; controls(); };
      oscillator.start(); oscillator.stop(context.currentTime + .9);
      el('tone-status').textContent = 'Playing a short test tone.';
    } catch (_) { if (token === toneToken) { stopTone(); el('tone-status').textContent = 'Sound could not start. Check browser sound permissions and your selected output.'; } }
  }
  function clearReport() { clearTimeout(reportTimer); if (reportURL) URL.revokeObjectURL(reportURL); reportURL = null; }
  function downloadReport() {
    clearReport();
    const lines = ['MuteMe meeting checklist', new Date().toLocaleString(), '', `Microphone: ${micObserved ? 'Signal detected (clarity not assessed)' : 'Not completed'}`, `Camera: ${el('skip-camera').checked ? 'Intentionally off' : cameraObserved ? 'Preview received' : 'Not completed'}`];
    if (states.mic.stream && audio && audio.state !== 'running') lines.push('Live microphone analysis: Paused by the browser; earlier observations do not establish a current audio level. Stop and restart the microphone test to retry.');
    for (const check of checks) lines.push(`${check.checked ? '[x]' : '[ ]'} ${check.parentElement.textContent.trim()}`);
    lines.push('', 'Checklist observations only. No audio or images included.', 'Confirm devices and mute state inside your meeting app.', 'https://muteme.com/pages/meeting-tools');
    try {
      reportURL = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' }));
      const link = document.createElement('a'); link.href = reportURL; link.download = 'muteme-meeting-checklist.txt'; root.append(link); link.click(); link.remove();
      reportTimer = setTimeout(clearReport, 60000);
      el('report-status').textContent = 'Checklist download requested. It contains no audio or images.';
    } catch (_) { clearReport(); el('report-status').textContent = 'This browser could not download the checklist. You can review the results on this page.'; }
  }
  function reset() {
    stopAll('Device is off. Start a new test when you are ready.');
    micObserved = false; cameraObserved = false; sustainedSignal = 0;
    checks.forEach(c => { c.checked = false; }); el('skip-camera').checked = false;
    el('signal').textContent = 'Not tested'; el('resolution').textContent = '—'; el('brightness').textContent = '—';
    el('tone-status').textContent = 'No sound is playing.';
    el('report-status').textContent = 'Your report contains checklist results only, with no audio or images.';
    clearReport(); summary();
  }
  for (const kind of ['mic', 'camera']) {
    el(`${kind}-start`).addEventListener('click', () => start(kind));
    el(`${kind}-stop`).addEventListener('click', () => stop(kind, 'Device access stopped. Your completed observations remain below.'));
    el(`${kind}-device`).addEventListener('change', () => {
      stop(kind);
      if (kind === 'mic') { micObserved = false; el('signal').textContent = 'Not tested'; } else { cameraObserved = false; el('resolution').textContent = '—'; el('brightness').textContent = '—'; }
      summary(); start(kind);
    });
  }
  el('stop-all').addEventListener('click', () => stopAll('Device access stopped. Your completed observations remain below.'));
  el('tone').addEventListener('click', playTone);
  el('download').addEventListener('click', downloadReport);
  el('reset').addEventListener('click', reset);
  checks.forEach(check => check.addEventListener('change', summary));
  el('skip-camera').addEventListener('change', () => { if (el('skip-camera').checked) stop('camera', 'Camera intentionally off for this meeting.'); summary(); });
  document.addEventListener('visibilitychange', () => { if (document.hidden) { stopAll('Device access stopped while the page is hidden. Restart the test to continue.'); el('tone-status').textContent = 'No sound is playing.'; } });
  window.addEventListener('pagehide', () => { stopAll(); clearReport(); });
  if (media && media.addEventListener) media.addEventListener('devicechange', devices);
  if (!supported) { status('mic', 'Device tests need HTTPS and a supported browser. Open this page in a full browser tab.'); status('camera', 'Device tests need HTTPS and a supported browser. The manual checklist still works.'); }
  else if (!AudioContextClass) status('mic', 'Live audio analysis is unavailable in this browser. Try a current browser with Web Audio support.');
  el('tone').disabled = !AudioContextClass;
  el('download').disabled = false;
  el('reset').disabled = false;
  controls(); summary();
})();
